/**
 * pwaOauthHandoff — small session-storage bridge for the Google OAuth
 * round-trip.
 *
 * When a user taps "Continue with Google" we redirect off-origin to the
 * Emergent auth service, which comes back to `/#session_id=…`. That
 * redirect strips any React state we might have wanted to keep. We
 * therefore stash a small, structured pending record in sessionStorage
 * BEFORE the redirect and read it back in AuthCallback.
 *
 * The record is a diagnostic complement to the server-side signed OAuth
 * state (services/oauth_state.py) — it does NOT authorise anything.
 * Server-side verification is still the security boundary. This record
 * exists so:
 *   - The callback knows which app to POST /api/auth/session with
 *   - We can correlate client-side and server-side log lines by flow_id
 *   - Stale/expired handoffs auto-clear (30-minute TTL)
 */
const KEY = "advoset:oauth:pending";
const MAX_AGE_MS = 30 * 60 * 1000; // 30 minutes

function nowIso() {
  return new Date().toISOString();
}

function newFlowId() {
  // Cryptographically random when available; falls back to a tolerable
  // pseudo-random for the very old browsers that lack `crypto`.
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    const bytes = new Uint8Array(16);
    if (window.crypto?.getRandomValues) window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  } catch (_) {
    return `f_${Date.now().toString(16)}_${Math.floor(Math.random() * 1e9).toString(16)}`;
  }
}

/** Record the app + flow the user is starting. Returns the flow_id. */
export function beginOauthHandoff(appSlug) {
  const record = {
    app: appSlug,
    flow_id: newFlowId(),
    started_at: nowIso(),
  };
  try {
    sessionStorage.setItem(KEY, JSON.stringify(record));
  } catch (_) {
    /* sessionStorage disabled — degrade gracefully */
  }
  return record;
}

/** Read + drop the record. Returns null if missing or expired. */
export function consumeOauthHandoff() {
  let raw = null;
  try { raw = sessionStorage.getItem(KEY); } catch (_) { return null; }
  if (!raw) return null;
  try { sessionStorage.removeItem(KEY); } catch (_) { /* ignore */ }
  let parsed;
  try { parsed = JSON.parse(raw); }
  catch (_) { return null; }
  if (!parsed?.app || !parsed?.started_at) return null;
  const started = Date.parse(parsed.started_at);
  if (!Number.isFinite(started)) return null;
  if (Date.now() - started > MAX_AGE_MS) return null;
  return parsed;
}
